import { Anchor, FieldSet, Heading, Legend, Paragraph } from '@zillow/constellation-10';
import { EXTERNAL_URLS } from '@zg-rentals/applications-utils';
import { useId, useIdVerificationCallbacks, useTermSet } from '../../../IdvContext';
import { ExitReason, TermSet } from '../../../../../models';
import {
  useIdVerificationWebApiHandler_acceptTerms,
  useIdVerificationWebApiHandler_getTermSet,
  useIdVerificationWebApiHandler_isUserIdentified,
} from '@zg-rentals/ts-id-verification-client-react-query';
import {
  type GetTermSetWebApiResponseDto,
  IdentificationStatusType,
  TermType,
  type WebApiTerm,
} from '@zg-rentals/ts-id-verification-client';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useContext, useEffect } from 'react';
import { useEffectOnce } from 'react-use';
import { LANDLORD_CLICKSTREAM_EVENTS } from '../../../clickstream/events';
import TermsCheckbox from './TermsCheckbox';
import NoticeToUsers from './NoticeToUsers';
import { termSetToTermSetId } from '../../../utils/termSetUtils';
import { getZgAuthZoneFromTermSet } from '../../../utils/zgAuthZoneUtils';
import { WizardContext } from '../../../wizard/WizardContext';

export function allTermsAccepted(terms: Array<WebApiTerm> | undefined) {
  if (!terms?.length) {
    return null;
  }
  return terms.every((term) => term.isAccepted && term.isCurrent);
}

export default function TermsStep() {
  const queryClient = useQueryClient();
  const termSet = useTermSet();
  const requestingClient = useId();
  const termSetId = termSetToTermSetId(termSet);
  const { setIsNextDisabled, setOnNext } = useContext(WizardContext);
  const getTermSetKey = useIdVerificationWebApiHandler_getTermSet.getKey({ termSetId });
  const { trackEvent, onExit } = useIdVerificationCallbacks();
  const { data: getTermSetData } = useIdVerificationWebApiHandler_getTermSet(
    { termSetId },
    {
      // The acceptTerms mutation always invalidates this query resulting in it being
      // stale and triggering a refetch
      staleTime: Infinity,
    },
  );

  const terms = getTermSetData?.terms ?? [];

  const { data: isUserIdentifiedData } = useIdVerificationWebApiHandler_isUserIdentified(
    {
      requestingClient,
      zgAuthZone: getZgAuthZoneFromTermSet(termSet),
    },
    {
      staleTime: Infinity,
    },
  );

  const { mutateAsync: markAcceptedTerms, isPending } = useIdVerificationWebApiHandler_acceptTerms({
    onMutate: async (body) => {
      const prevState = queryClient.getQueryData(getTermSetKey);
      queryClient.setQueryData(getTermSetKey, (previous: unknown) => {
        const prev = previous as GetTermSetWebApiResponseDto;
        const newData: GetTermSetWebApiResponseDto = {
          terms: prev.terms.map((termData) => {
            const matchingTerm = body?.postData.termsToAccept.find((term) => {
              return term.termType === termData.id;
            });

            if (matchingTerm) {
              return {
                ...termData,
                acceptedVersion: matchingTerm.version,
                isAccepted: matchingTerm.version === -1 ? false : true,
                isCurrent: matchingTerm.version === -1 ? false : true,
              };
            }

            return termData;
          }),
        };
        return newData;
      });
      return prevState;
    },
    onSettled() {
      queryClient.invalidateQueries({ queryKey: getTermSetKey });
    },
  });

  const writtenConsentTerms = terms.filter((term) => {
    return (
      term.id === TermType.EXPERIAN_CIC_WRITTEN_CONSENT_RENTER || term.id === TermType.EXPERIAN_WRITTEN_CONSENT_LANDLORD
    );
  });

  const otherTerms = terms.filter((term) => {
    return (
      term.id !== TermType.EXPERIAN_CIC_WRITTEN_CONSENT_RENTER && term.id !== TermType.EXPERIAN_WRITTEN_CONSENT_LANDLORD
    );
  });

  useEffect(() => {
    setIsNextDisabled(!allTermsAccepted(terms) || isPending);
    return () => setIsNextDisabled(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified for staleness
  }, [terms, isPending, setIsNextDisabled]);

  useEffect(() => {
    if (isUserIdentifiedData?.identificationStatusType === IdentificationStatusType.CURRENTLY_IDENTIFIED) {
      setOnNext(() => async () => onExit({ reason: ExitReason.CurrentlyIdentifiedAcceptedNewTerms, success: true }));
    }
    return () => setOnNext(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified for staleness
  }, [onExit, setOnNext]);

  useEffectOnce(() => {
    if (termSet === TermSet.LandlordScreening) {
      trackEvent(LANDLORD_CLICKSTREAM_EVENTS.displayTerms);
      trackEvent(LANDLORD_CLICKSTREAM_EVENTS.viewPage('idv_terms_and_conditions'));
    }
  });

  return (
    <>
      <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default' }}>
        Agree to terms and conditions
      </Heading>
      <Paragraph css={{ marginBottom: 'default' }}>
        Before starting your ID verification, you’ll need to review and agree to our third party partners’ requirements
        for viewing personal information. This provides security for both you and your applicants.{' '}
        <Anchor
          href={
            termSet === TermSet.LandlordScreening
              ? EXTERNAL_URLS.LANDLORD_CANNOT_IDV_HELP
              : EXTERNAL_URLS.RENTER_CANNOT_IDV_HELP
          }
          target="_blank"
          rel="noopener noreferrer"
        >
          Read more about identity verification requirements
        </Anchor>
      </Paragraph>
      <Paragraph css={{ marginBottom: 'default' }}>
        Review the terms and policies carefully before agreeing to each statement.
      </Paragraph>
      <Paragraph css={{ marginBottom: 'default' }}>
        Zillow Group will not store or use any of the entered information.
      </Paragraph>
      <FieldSet required legend={<Legend>To continue, accept the following terms and policies:</Legend>}>
        <TermsCheckbox terms={writtenConsentTerms} markAcceptedTerms={markAcceptedTerms} />
        <TermsCheckbox terms={otherTerms} markAcceptedTerms={markAcceptedTerms} />
      </FieldSet>
      <NoticeToUsers />
    </>
  );
}
